import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Container, Typography, useTheme } from "@mui/material";
import { getLocalStorageItem, groupBy, setLocalStorageItem } from "../../_helpers";
import { useTranslation } from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import { useSelector } from "react-redux";
import { getProjectsInfoForOverview } from "../../apis/buyersGuideApis";
import DataGridV2 from "../../components/DataGridV2";
import { apps, LOCAL_STORAGE_GRID_KEYS } from "../../_constants";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { INITIAL_ROW_SELECTION_MODEL } from "../../components/DataGridV2/constants";
import { getProjectOverviewColumns } from "./projectOverviewUtils";

const ProjectOverview = (props) => {
  const { history } = props;
  const { t , i18n } = useTranslation();
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const { all = [], loading: buildingLoading } = useSelector((state) => state.buildings);
  const { messageCountPerProject, savedMessagesCountPerProject } = useSelector((state) => state.dashboardCount);
  const app = useSelector((state) => state.app);
  const keyPrefix = app === apps.huisinfo ? "BUYERS_GUIDE" : "GENERAL";
  const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);
  const [paginationModel, setPaginationModel] = useState(() => getLocalStorageItem(
          LOCAL_STORAGE_GRID_KEYS.PROJECT_OVERVIEW_PAGINATION[keyPrefix], undefined
      ))

  useEffect(() => {
    setProjects([]);
    setLoading(true);
    const projectsGroup = groupBy(all, "projectId");
    const projectIds = Object.keys(projectsGroup);
    if (!buildingLoading) {
      getProjectsInfoForOverview(projectIds).then(({ data: response }) => {
        const data = [];
        for (let i = 0; i < projectIds.length; i++) {
          const projectId = projectIds[i];
          const project = response.find((p) => p.projectId === projectId);
          if (project) {
            data.push({
              ...project,
              id: projectId,
              noOfUnreadChats: getNoOfUnreadChats(projectId),
              noOfUnreadMessages: getNoOfUnreadMessages(projectId),
              noOfSavedMessages: getNoOfSavedMessages(projectId),
            });
          }
        }
        setProjects(data);
        setLoading(false);
      });
    }
    return () => {
      setProjects([]);
    };
  }, [history.location.key, all, app, buildingLoading]);

  useEffect(() => {
    setProjects(
      projects.map((p) => ({
        ...p,
        id: p.projectId,
        noOfUnreadChats: getNoOfUnreadChats(p.projectId),
        noOfUnreadMessages: getNoOfUnreadMessages(p.projectId),
        noOfSavedMessages: getNoOfSavedMessages(p.projectId),
      }))
    );
  }, [messageCountPerProject, savedMessagesCountPerProject]);


  const getNoOfUnreadChats = (projectId) => {
    if (messageCountPerProject) {
      const object = messageCountPerProject.find((x) => x.projectId.toUpperCase() === projectId.toUpperCase());
      return object ? object.noOfChats : 0;
    }
    return 0;
  };

  const getNoOfUnreadMessages = (projectId) => {
    if (messageCountPerProject) {
      const object = messageCountPerProject.find((x) => x.projectId.toUpperCase() === projectId.toUpperCase());
      return object ? object.count : 0;
    }
    return 0;
  };

  const getNoOfSavedMessages = (projectId) => {
    if (savedMessagesCountPerProject) {
      const object = savedMessagesCountPerProject.find((x) => x.projectId.toUpperCase() === projectId.toUpperCase());
      return object ? object.count : 0;
    }
    return 0;
  };

  const columns = getProjectOverviewColumns({t,keyPrefix,i18n,apiRef,theme,all})
 
  const onPaginationModelChange = useCallback((newPaginationModel) => {
    setPaginationModel(newPaginationModel)
    setLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.PROJECT_OVERVIEW_PAGINATION[keyPrefix],
      newPaginationModel
    );
  }, [keyPrefix])

  if (buildingLoading)
    return (
      <div className={classes.loader}>
        <CircularProgress size={25} />
      </div>
    );

  const getProjectOverviewPrintAndDownload = ({ isAllSelected = true, idsToExport = [] }) => {
          const userSelectedRows = isAllSelected ? projects : idsToExport.map(id => projects?.find(item => item?.projectId === id));
          return userSelectedRows;
  };

  return (
    <Container maxWidth={false} className={classes.mainContainer}>
      <Box display="flex" flexDirection="column" sx={{ height: "100%" }}>
        <Box p={2} display={'flex'} alignItems={'center'} >
          <Typography variant='h6'>{t("general.projects")}</Typography>
        </Box>
        <Box  sx={{ flex: 1, minHeight: 0 }}>
      <DataGridV2
        localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.PROJECT_OVERVIEW_SEARCH_TEXT[keyPrefix]}
        localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.PROJECT_OVERVIEW_COLUMN_FILTER[keyPrefix]}
        localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS.PROJECT_OVERVIEW_COLUMN_SORT[keyPrefix]}
        localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.PROJECT_OVERVIEW_COLUMN_ORDER[keyPrefix]}
        localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.PROJECT_OVERVIEW_COLUMN_WIDTH[keyPrefix]}
        localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.PROJECT_OVERVIEW_COLUMN_VISIBILITY[keyPrefix]}
        apiRef={apiRef}
        checkboxSelection
        loading={loading}
        rows={ projects}
        columns={columns}
        pagination
        filterMode="client"
        {...(!!paginationModel && { paginationModel })}
        onPaginationModelChange={onPaginationModelChange}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
        toolbarProps={{
          selectedRows: rowSelectionModel,
          getData: getProjectOverviewPrintAndDownload
        }}
      />
      </Box>
      </Box>
    </Container>
  );
};

const useStyles = makeStyles((theme) => ({
  "@global": {
    ".MuiButtonBase-root:focus": {
      outline: 0,
    },
  },
  mainContainer: {
    height: "100%",
    width: "100%",
    overflow: "visible",
    padding: 0,
  },
  grow: {
    flexGrow: 1,
  },
  loader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  greenText: {
    color: theme.palette.success.main,
  },
}));

export { ProjectOverview };
